import React, { useState } from 'react';
import { Search, Building2, Award, Loader2, AlertCircle, CheckCircle, Edit2, Save, X, Sliders, ArrowRight, Clock, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { calculateScore, extractDegreePrefix, SEARCH_CONFIG, getPrefixVariant } from '../utils/rncpSearchUtils';

const TestFusionOnboarding = () => {
  const [siret, setSiret] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('siret'); // siret, formations, enrichment, verification, analysis, comparison
  const [organism, setOrganism] = useState(null);
  const [formations, setFormations] = useState([]);
  const [enrichedFormations, setEnrichedFormations] = useState([]);
  const [fusionResults, setFusionResults] = useState([]);
  const [similarityThreshold, setSimilarityThreshold] = useState(66);
  const [editingRncp, setEditingRncp] = useState(null);
  const [manualRncp, setManualRncp] = useState('');
  const [comparisonData, setComparisonData] = useState(null);
  const [showOnlyFused, setShowOnlyFused] = useState(true);

  // Fonction de scoring client (utilise la même fonction que /intercariforef-test)
  const calculateClientScore = (query, title, rncpCode = '') => {
    // Validation des paramètres
    if (!query || !title) {
      console.warn('calculateClientScore: query ou title manquant', { query, title });
      return 0;
    }

    // Utiliser la fonction de scoring de rncpSearchUtils
    return calculateScore(query, title, rncpCode, true);
  };

  // Formatage SIRET
  const formatSiret = (value) => {
    const numbers = value.replace(/\s/g, '');
    if (numbers.length <= 14) {
      return numbers.replace(/(\d{3})(\d{3})?(\d{3})?(\d{2})?(\d{3})?/, (match, p1, p2, p3, p4, p5) => {
        let result = p1;
        if (p2) result += ' ' + p2;
        if (p3) result += ' ' + p3;
        if (p4) result += ' ' + p4;
        if (p5) result += ' ' + p5;
        return result;
      });
    }
    return value;
  };

  // Étape 1: Rechercher l'organisme et les formations
  const handleSearchOrganism = async () => {
    const cleanSiret = siret.replace(/\s/g, '');

    if (!cleanSiret || cleanSiret.length !== 14 || !/^\d{14}$/.test(cleanSiret)) {
      toast.error('Le SIRET doit contenir exactement 14 chiffres');
      return;
    }

    setLoading(true);
    try {
      // Scraper Intercariforef
      const scrapeResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/intercariforef/scrape`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ siret: cleanSiret })
        }
      );

      const scrapeData = await scrapeResponse.json();

      if (!scrapeData.success || !scrapeData.data) {
        toast.error('Organisme non trouvé');
        return;
      }

      setOrganism({
        name: scrapeData.data.organism?.name || 'Organisme trouvé',
        siret: cleanSiret,
        totalFormations: scrapeData.data.totalFormations
      });

      // Récupérer les formations
      const formationsResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/intercariforef/formations`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            siret: cleanSiret,
            scrapeSource: 'intercariforef'
          })
        }
      );

      const formationsData = await formationsResponse.json();

      if (formationsData.success && formationsData.formations) {
        // Dédupliquer
        const seen = new Set();
        const uniqueFormations = formationsData.formations.filter(f => {
          const key = f.title.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        // Rechercher les RNCP pour chaque formation (MULTI-RECHERCHE comme /intercariforef-test)
        const formationsWithRncp = await Promise.all(
          uniqueFormations.map(async (formation) => {
            try {
              const headers = {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              };
              const baseUrl = `${import.meta.env.VITE_API_URL}/kit-apprentissage/search/intitule`;

              // Extraire le préfixe
              const { prefix, coreQuery } = extractDegreePrefix(formation.title);
              const apiSearchTerm = formation.title;

              // Préparer les recherches multiples
              const searchPromises = [];

              // TYPE 1: Recherche complète
              searchPromises.push(
                fetch(`${baseUrl}?q=${encodeURIComponent(apiSearchTerm)}&limit=100`, { headers })
                  .then(r => r.json())
                  .catch(() => ({ success: false, data: [] }))
              );

              // TYPE 2: Sans préfixe
              if (prefix && coreQuery && coreQuery !== apiSearchTerm) {
                searchPromises.push(
                  fetch(`${baseUrl}?q=${encodeURIComponent(coreQuery)}&limit=100`, { headers })
                    .then(r => r.json())
                    .catch(() => ({ success: false, data: [] }))
                );
              }

              // TYPE 5: 2 premiers mots spécifiques
              const { stopWords, genericWords } = SEARCH_CONFIG;
              const queryForKeywords = (prefix ? coreQuery : apiSearchTerm)
                .replace(/-/g, ' ')
                .replace(/[,;:.!?()]/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();

              const words = queryForKeywords.split(' ')
                .filter(w => w.length > 3 && !stopWords.includes(w.toLowerCase()))
                .map((w, index) => ({
                  word: w,
                  isGeneric: genericWords.includes(w.toLowerCase())
                }));

              const specificWords = words.filter(w => !w.isGeneric);

              if (specificWords.length >= 2) {
                const keyWords2 = specificWords.slice(0, 2).map(w => w.word).join(' ');
                if (keyWords2 && keyWords2 !== apiSearchTerm && keyWords2 !== coreQuery) {
                  searchPromises.push(
                    fetch(`${baseUrl}?q=${encodeURIComponent(keyWords2)}&limit=100`, { headers })
                      .then(r => r.json())
                      .catch(() => ({ success: false, data: [] }))
                  );
                }
              }

              // TYPE 6: Préfixe + 1er mot
              if (prefix && specificWords.length >= 1) {
                const queryWithPrefix = `${prefix} ${specificWords[0].word}`;
                searchPromises.push(
                  fetch(`${baseUrl}?q=${encodeURIComponent(queryWithPrefix)}&limit=100`, { headers })
                    .then(r => r.json())
                    .catch(() => ({ success: false, data: [] }))
                );
              }

              // TYPE 7: Variante de préfixe
              if (prefix) {
                const prefixVariant = getPrefixVariant(prefix);
                if (prefixVariant && prefixVariant !== prefix.toUpperCase()) {
                  const queryWithVariant = `${prefixVariant} ${coreQuery}`;
                  searchPromises.push(
                    fetch(`${baseUrl}?q=${encodeURIComponent(queryWithVariant)}&limit=100`, { headers })
                      .then(r => r.json())
                      .catch(() => ({ success: false, data: [] }))
                  );
                }
              }

              // Exécuter toutes les recherches en parallèle
              const responses = await Promise.all(searchPromises);

              // Combiner et dédupliquer par RNCP
              let allResults = [];
              const seenRncps = new Set();

              responses.forEach(response => {
                if (response.success && response.data) {
                  const certs = Array.isArray(response.data) ? response.data : [response.data];
                  certs.forEach(cert => {
                    const rncpCode = cert.rncp || cert.code_rncp;
                    if (rncpCode && !seenRncps.has(rncpCode)) {
                      seenRncps.add(rncpCode);
                      allResults.push(cert);
                    }
                  });
                }
              });

              // Calculer le score pour chaque résultat
              const scoredResults = allResults.map(cert => {
                const title = cert.intitule || cert.intitule_diplome || cert.intitule_rncp || cert.title;
                const rncpCode = cert.rncp || cert.code_rncp;
                const score = calculateClientScore(formation.title, title, rncpCode);
                return { ...cert, relevanceScore: score };
              })
              .filter(cert => cert.relevanceScore >= 35)
              .sort((a, b) => b.relevanceScore - a.relevanceScore);

              if (scoredResults.length > 0) {
                const bestMatch = scoredResults[0];
                const rncpCode = bestMatch.rncp || bestMatch.code_rncp;
                const title = bestMatch.intitule || bestMatch.intitule_diplome || bestMatch.intitule_rncp || bestMatch.title;

                return {
                  ...formation,
                  suggestedRncp: rncpCode,
                  suggestedTitle: title,
                  score: bestMatch.relevanceScore
                };
              }
            } catch (error) {
              console.error('Erreur recherche RNCP:', error);
            }

            return {
              ...formation,
              suggestedRncp: null,
              suggestedTitle: null,
              score: 0
            };
          })
        );

        setFormations(formationsWithRncp);
        setStep('formations');
        toast.success(`${formationsWithRncp.length} formations trouvées`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  // Éditer manuellement le RNCP
  const handleEditRncp = (index) => {
    setEditingRncp(index);
    setManualRncp(formations[index].suggestedRncp || '');
  };

  const handleSaveRncp = (index) => {
    const updated = [...formations];
    updated[index].suggestedRncp = manualRncp;
    updated[index].score = 100; // Score manuel = 100%
    setFormations(updated);
    setEditingRncp(null);
    setManualRncp('');
  };

  // Étape 2: Enrichir avec les compétences (scraping)
  const handleEnrichFormations = async () => {
    setLoading(true);
    setStep('enrichment');

    try {
      const enriched = [];
      const formationsToEnrich = formations.filter(f => f.suggestedRncp);

      for (let i = 0; i < formationsToEnrich.length; i++) {
        const formation = formationsToEnrich[i];
        const fullRncp = formation.suggestedRncp.toUpperCase().startsWith('RNCP')
          ? formation.suggestedRncp
          : `RNCP${formation.suggestedRncp}`;

        console.log(`[${i + 1}/${formationsToEnrich.length}] Enrichissement: ${formation.title} (${fullRncp})`);

        try {
          // Utiliser le même endpoint de scraping que /test-scraping
          const numeroRncp = fullRncp.replace(/^RNCP/i, '');

          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/rncp-scraping/scrape`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ numeroRncp })
            }
          );

          const data = await response.json();

          if (data.success && data.data) {
            // Extraire les compétences des blocs
            let competences = [];
            let blocks = [];

            if (data.data.blocsCompetences && Array.isArray(data.data.blocsCompetences)) {
              blocks = data.data.blocsCompetences.map(bloc => ({
                code: bloc.code,
                libelle: bloc.libelle,
                competences: bloc.competences || []
              }));

              // Extraire toutes les compétences de tous les blocs
              data.data.blocsCompetences.forEach(bloc => {
                if (bloc.competences && Array.isArray(bloc.competences)) {
                  bloc.competences.forEach(comp => {
                    const compText = typeof comp === 'string' ? comp : (comp.libelle || comp.description || '');
                    if (compText) {
                      competences.push(compText);
                    }
                  });
                }
              });
            }

            if (competences.length > 0) {
              const formationEnriched = {
                ...formation,
                rncpCode: formation.suggestedRncp,
                competences: competences,
                blocks: blocks,
                intitule: data.data.intitule
              };
              enriched.push(formationEnriched);

              // Mettre à jour progressivement pour afficher l'avancement
              setEnrichedFormations([...enriched]);
            } else {
              console.warn(`Aucune compétence trouvée pour ${fullRncp}`);
            }
          } else {
            console.warn(`Données non valides pour ${fullRncp}:`, data);
          }
        } catch (error) {
          console.error(`Erreur pour ${fullRncp}:`, error);
        }

        // Délai entre chaque formation
        if (i < formationsToEnrich.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // Passer à l'étape de vérification au lieu d'aller directement à l'analyse
      setStep('verification');
      toast.success(`${enriched.length} formations enrichies - Vérifiez les compétences avant l'analyse`);
    } catch (error) {
      console.error('Erreur enrichissement:', error);
      toast.error('Erreur lors de l\'enrichissement');
    } finally {
      setLoading(false);
    }
  };

  // Calculer la similarité entre deux textes (Jaccard sur les mots)
  const calculateSimilarity = (text1, text2) => {
    const normalize = (text) => {
      return text.toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\w\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    };

    const stopWords = ['le', 'la', 'les', 'un', 'une', 'des', 'de', 'du', 'et', 'ou', 'en', 'a', 'au', 'aux', 'pour', 'par', 'dans', 'sur', 'avec'];

    const getWords = (text) => {
      return normalize(text)
        .split(/\s+/)
        .filter(w => w.length > 2 && !stopWords.includes(w));
    };

    const words1 = new Set(getWords(text1));
    const words2 = new Set(getWords(text2));

    const intersection = new Set([...words1].filter(w => words2.has(w)));
    const union = new Set([...words1, ...words2]);

    return union.size > 0 ? Math.round((intersection.size / union.size) * 100) : 0;
  };

  // Étape 3: Analyser les fusions (algorithme du rapport technique)
  const handleAnalyzeFusions = () => {
    setLoading(true);

    try {
      // Créer une liste plate de toutes les compétences avec leur contexte
      const allCompetences = [];
      enrichedFormations.forEach(formation => {
        formation.competences.forEach(comp => {
          allCompetences.push({
            description: comp,
            certification_id: formation.rncpCode,
            certificationTitle: formation.title
          });
        });
      });

      // Algorithme de fusion du rapport technique
      const fused = [];
      const processed = new Set();

      allCompetences.forEach((comp, index) => {
        if (processed.has(index)) return;

        const similar = [comp];
        const similarIndices = [index];

        // Chercher les compétences similaires
        for (let i = index + 1; i < allCompetences.length; i++) {
          if (processed.has(i)) continue;

          // ⚠️ RÈGLE CRITIQUE : NE PAS fusionner si même certification
          if (comp.certification_id === allCompetences[i].certification_id) {
            continue;
          }

          // Calculer similarité
          const similarity = calculateSimilarity(
            comp.description,
            allCompetences[i].description
          );

          // Décision de fusion selon le seuil
          const shouldMerge = similarityThreshold === 100
            ? comp.description.trim().toLowerCase() === allCompetences[i].description.trim().toLowerCase()
            : similarity >= similarityThreshold;

          if (shouldMerge) {
            similar.push(allCompetences[i]);
            similarIndices.push(i);
          }
        }

        // Marquer comme traité
        similarIndices.forEach(idx => processed.add(idx));

        // Créer nœud fusionné si plusieurs compétences similaires
        if (similar.length > 1) {
          fused.push({
            isMerged: true,
            count: similar.length,
            description: comp.description, // Prendre la première comme description principale
            sources: similar.map(s => ({
              rncp: s.certification_id,
              formation: s.certificationTitle,
              text: s.description
            })),
            // Calculer la similarité moyenne entre toutes les sources
            avgSimilarity: Math.round(
              similar.reduce((sum, s1, idx1) => {
                return sum + similar.slice(idx1 + 1).reduce((innerSum, s2) => {
                  return innerSum + calculateSimilarity(s1.description, s2.description);
                }, 0);
              }, 0) / ((similar.length * (similar.length - 1)) / 2)
            )
          });
        } else {
          // Compétence unique (pas de fusion)
          fused.push({
            isMerged: false,
            description: comp.description,
            rncp: comp.certification_id,
            formation: comp.certificationTitle
          });
        }
      });

      // Trier les résultats : fusionnés en premier, par nombre de sources décroissant
      const sorted = fused
        .filter(f => f.isMerged) // Garder uniquement les fusionnés
        .sort((a, b) => b.count - a.count); // Trier par nombre de sources décroissant

      setFusionResults(sorted);

      const mergedCount = fused.filter(f => f.isMerged).length;
      const totalCompetences = fused.length;

      toast.success(`Analyse terminée : ${mergedCount} compétences fusionnées`);
    } catch (error) {
      console.error('Erreur analyse:', error);
      toast.error('Erreur lors de l\'analyse');
    } finally {
      setLoading(false);
    }
  };

  // Étape 4: Préparer la comparaison des 2 formations avec le plus de similitudes
  const handlePrepareComparison = () => {
    if (!fusionResults || fusionResults.length === 0 || enrichedFormations.length < 2) {
      toast.error('Pas assez de données pour la comparaison');
      return;
    }

    // Calculer le nombre de compétences fusionnées entre chaque paire de formations
    const formationPairs = [];

    for (let i = 0; i < enrichedFormations.length; i++) {
      for (let j = i + 1; j < enrichedFormations.length; j++) {
        const formation1 = enrichedFormations[i];
        const formation2 = enrichedFormations[j];

        // Compter les compétences fusionnées entre ces 2 formations
        const sharedFusions = fusionResults.filter(fusion => {
          const rncps = fusion.sources.map(s => s.rncp);
          return rncps.includes(formation1.rncpCode) && rncps.includes(formation2.rncpCode);
        });

        if (sharedFusions.length > 0) {
          formationPairs.push({
            formation1,
            formation2,
            sharedFusionCount: sharedFusions.length,
            sharedFusions
          });
        }
      }
    }

    if (formationPairs.length === 0) {
      toast.error('Aucune compétence commune trouvée entre les formations');
      return;
    }

    // Trier par nombre de fusions partagées (décroissant)
    formationPairs.sort((a, b) => b.sharedFusionCount - a.sharedFusionCount);

    // Prendre la paire avec le plus de similitudes
    const topPair = formationPairs[0];

    // Calculer les métriques
    const totalCompetences1 = topPair.formation1.competences.length;
    const totalCompetences2 = topPair.formation2.competences.length;
    const fusedCount = topPair.sharedFusionCount;
    const totalFormations = enrichedFormations.length;
    const unificationRate = Math.round((fusedCount / Math.min(totalCompetences1, totalCompetences2)) * 100);
    const timeSaved = fusedCount * 4; // 4h par compétence

    setComparisonData({
      formation1: topPair.formation1,
      formation2: topPair.formation2,
      sharedFusions: topPair.sharedFusions,
      fusedCount,
      unificationRate,
      timeSaved,
      totalFormations
    });

    setStep('comparison');
    toast.success(`Analyse terminée : ${fusedCount} compétences communes identifiées`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            🧪 Test Fusion Onboarding
          </h1>
          <p className="text-gray-600">
            Analyse complète des opportunités de fusion de compétences
          </p>
        </div>

        {/* Étape 1: SIRET */}
        {step === 'siret' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Building2 className="w-6 h-6 text-blue-600" />
              Étape 1 : Rechercher l'organisme
            </h2>

            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SIRET de l'organisme
                </label>
                <input
                  type="text"
                  value={siret}
                  onChange={(e) => setSiret(formatSiret(e.target.value))}
                  placeholder="XXX XXX XXX XX XXX"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleSearchOrganism}
                  disabled={loading}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Recherche...
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5" />
                      Rechercher
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Étape 2: Liste des formations avec RNCP */}
        {step === 'formations' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Award className="w-6 h-6 text-purple-600" />
                Étape 2 : Vérifier les codes RNCP
              </h2>
              <p className="text-gray-600 mt-2">
                {organism?.name} - {formations.length} formations trouvées
              </p>
            </div>

            <div className="space-y-4 mb-6">
              {formations.map((formation, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-2">
                        {formation.title}
                      </h3>
                      <div className="flex items-center gap-4">
                        {editingRncp === index ? (
                          <>
                            <input
                              type="text"
                              value={manualRncp}
                              onChange={(e) => setManualRncp(e.target.value)}
                              placeholder="RNCP35304"
                              className="px-3 py-1 border border-gray-300 rounded"
                            />
                            <button
                              onClick={() => handleSaveRncp(index)}
                              className="text-green-600 hover:text-green-700"
                            >
                              <Save className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => setEditingRncp(null)}
                              className="text-gray-600 hover:text-gray-700"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-600">RNCP:</span>
                              <span className={`font-mono font-semibold ${formation.score === 100 ? 'text-green-600' : formation.score >= 70 ? 'text-blue-600' : 'text-orange-600'}`}>
                                {formation.suggestedRncp || 'Non trouvé'}
                              </span>
                              {formation.suggestedRncp && (
                                <span className="text-xs text-gray-500">
                                  (Score de confiance: {Math.round(formation.score)}%)
                                </span>
                              )}
                            </div>
                            {formation.score < 100 && (
                              <button
                                onClick={() => handleEditRncp(index)}
                                className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                              >
                                <Edit2 className="w-4 h-4" />
                                Modifier
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div>
                      {formation.score === 100 ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : formation.score >= 70 ? (
                        <CheckCircle className="w-6 h-6 text-blue-600" />
                      ) : (
                        <AlertCircle className="w-6 h-6 text-orange-600" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {formations.some(f => !f.suggestedRncp) && (
              <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-800">
                  ⚠️ Certaines formations n'ont pas de code RNCP. Elles seront ignorées lors de l'enrichissement.
                  Vous pouvez les modifier manuellement ou continuer sans elles.
                </p>
              </div>
            )}

            <button
              onClick={handleEnrichFormations}
              disabled={loading || formations.filter(f => f.suggestedRncp).length === 0}
              className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Enrichissement en cours...
                </>
              ) : (
                'Enrichir avec les compétences'
              )}
            </button>
          </div>
        )}

        {/* Étape 2.5: Enrichissement en cours */}
        {step === 'enrichment' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
              Enrichissement des formations
            </h2>

            <p className="text-gray-600 mb-6">
              Récupération des blocs de compétences et compétences détaillées depuis France Compétences (scraping)...
            </p>

            {/* Afficher les formations enrichies progressivement */}
            {enrichedFormations.length > 0 && (
              <div className="space-y-4">
                {enrichedFormations.map((formation, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">{formation.title}</h3>
                        <span className="text-sm text-gray-600">
                          {formation.rncpCode} - {formation.competences.length} compétences trouvées
                        </span>
                      </div>
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                        ✓ Enrichie
                      </span>
                    </div>

                    {/* Afficher les blocs de compétences avec leurs compétences détaillées */}
                    {formation.blocks && formation.blocks.length > 0 && (
                      <div className="mt-3 space-y-3">
                        {formation.blocks.map((bloc, bIndex) => (
                          <div key={bIndex} className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg overflow-hidden">
                            {/* En-tête du bloc */}
                            <div className="px-4 py-3">
                              <h3 className="text-sm font-semibold text-white">
                                {bloc.code} - {bloc.libelle}
                              </h3>
                              <p className="text-blue-100 text-xs mt-1">
                                {bloc.competences.length} compétence{bloc.competences.length > 1 ? 's' : ''}
                              </p>
                            </div>

                            {/* Liste des compétences */}
                            {bloc.competences.length > 0 && (
                              <div className="px-4 py-3 bg-white">
                                <ul className="space-y-2">
                                  {bloc.competences.map((competence, compIndex) => (
                                    <li key={compIndex} className="flex items-start">
                                      <span className="inline-flex items-center justify-center w-5 h-5 mr-2 text-xs font-medium text-blue-600 bg-blue-100 rounded-full flex-shrink-0 mt-0.5">
                                        {compIndex + 1}
                                      </span>
                                      <span className="text-xs text-gray-700 flex-1">
                                        {competence}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {loading && (
              <div className="mt-6 text-center text-gray-600">
                <p>Veuillez patienter, cela peut prendre quelques minutes...</p>
              </div>
            )}
          </div>
        )}

        {/* Étape 2.5 BIS: Vérification des compétences */}
        {step === 'verification' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-green-600" />
              Étape 2.5 : Vérifier les compétences récupérées
            </h2>

            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                ✅ <strong>{enrichedFormations.length} formations enrichies</strong> avec{' '}
                <strong>{enrichedFormations.reduce((sum, f) => sum + f.competences.length, 0)} compétences</strong> au total
              </p>
              <p className="text-xs text-green-700 mt-2">
                Vérifiez que les compétences ont bien été récupérées avant de lancer l'analyse de fusion.
              </p>
            </div>

            {/* Liste détaillée des formations enrichies */}
            <div className="space-y-4 mb-6 max-h-[500px] overflow-y-auto">
              {enrichedFormations.map((formation, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">{formation.title}</h3>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                          {formation.rncpCode}
                        </span>
                        <span className="text-sm text-gray-600">
                          {formation.competences.length} compétences
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Afficher TOUTES les compétences (dépliable) */}
                  <details className="mt-3">
                    <summary className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700">
                      Voir toutes les compétences détaillées
                    </summary>
                    <ul className="mt-3 space-y-2 pl-4">
                      {formation.competences.map((comp, cIndex) => (
                        <li key={cIndex} className="text-sm text-gray-700 border-l-2 border-gray-300 pl-3">
                          {comp}
                        </li>
                      ))}
                    </ul>
                  </details>
                </div>
              ))}
            </div>

            <button
              onClick={() => setStep('analysis')}
              className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
            >
              Passer à l'analyse de fusion
            </button>
          </div>
        )}

        {/* Étape 3: Analyse des fusions */}
        {step === 'analysis' && (
          <div className="space-y-6">
            {/* Contrôles d'analyse */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Sliders className="w-6 h-6 text-green-600" />
                Étape 3 : Analyser les fusions
              </h2>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Seuil de similarité : {similarityThreshold}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={similarityThreshold}
                  onChange={(e) => setSimilarityThreshold(parseInt(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0% - Toutes les différences</span>
                  <span>100% - Identique uniquement</span>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  💡 Recommandation : 60-80% pour des résultats équilibrés
                </p>
              </div>

              <button
                onClick={handleAnalyzeFusions}
                disabled={loading}
                className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Analyse en cours...
                  </>
                ) : (
                  'Lancer l\'analyse de fusion'
                )}
              </button>

              {enrichedFormations.length > 0 && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    ✅ {enrichedFormations.length} formations enrichies avec{' '}
                    {enrichedFormations.reduce((sum, f) => sum + f.competences.length, 0)} compétences au total
                  </p>
                </div>
              )}
            </div>

            {/* Résultats des fusions */}
            {fusionResults.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-xl font-bold mb-6">
                  🎯 Résultats de la fusion ({fusionResults.length} compétences totales)
                </h3>

                <div className="mb-4 flex gap-4">
                  <div className="px-4 py-2 bg-green-100 rounded-lg">
                    <span className="text-sm font-semibold text-green-700">
                      {fusionResults.filter(f => f.isMerged).length} compétences fusionnées
                    </span>
                  </div>
                </div>

                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {fusionResults.map((item, index) => (
                    <div key={index}>
                      {item.isMerged ? (
                        // Nœud fusionné (plusieurs sources)
                        <div className="border-2 border-green-300 bg-green-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full">
                                🔗 FUSIONNÉ
                              </span>
                              <span className="text-sm text-green-700 font-semibold">
                                {item.count} sources ({item.avgSimilarity}% similarité moyenne)
                              </span>
                            </div>
                          </div>

                          {/* Description principale */}
                          <div className="mb-3 p-3 bg-white rounded-lg border border-green-200">
                            <p className="text-sm font-medium text-gray-900">
                              📝 Compétence unifiée :
                            </p>
                            <p className="text-sm text-gray-800 mt-2">
                              {item.description}
                            </p>
                          </div>

                          {/* Sources */}
                          <details className="mt-3">
                            <summary className="cursor-pointer text-sm font-medium text-green-700 hover:text-green-800">
                              Voir les {item.count} sources détaillées
                            </summary>
                            <div className="mt-3 space-y-2">
                              {item.sources.map((source, sIndex) => (
                                <div key={sIndex} className="p-3 bg-white border border-gray-200 rounded">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                                      {source.rncp}
                                    </span>
                                    <span className="text-xs text-gray-600">{source.formation}</span>
                                  </div>
                                  <p className="text-sm text-gray-700">• {source.text}</p>
                                </div>
                              ))}
                            </div>
                          </details>
                        </div>
                      ) : (
                        // Compétence unique (pas de fusion)
                        <div className="border border-gray-200 bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs font-semibold rounded">
                              UNIQUE
                            </span>
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                              {item.rncp}
                            </span>
                            <span className="text-xs text-gray-600">{item.formation}</span>
                          </div>
                          <p className="text-sm text-gray-700">• {item.description}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Bouton pour passer à l'étape 4 */}
                {fusionResults.length > 0 && enrichedFormations.length >= 2 && (
                  <button
                    onClick={handlePrepareComparison}
                    className="w-full mt-6 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                  >
                    <ArrowRight className="w-5 h-5" />
                    Analyser les similitudes entre formations
                  </button>
                )}
              </div>
            )}

            {fusionResults.length === 0 && !loading && (
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  Aucun résultat d'analyse disponible.
                  <br />
                  Cliquez sur "Lancer l'analyse de fusion" pour commencer.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Étape 4: Comparaison des 2 formations avec le plus de similitudes */}
        {step === 'comparison' && comparisonData && (
          <div className="space-y-6">
            {/* En-tête avec métriques */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Award className="w-6 h-6 text-blue-600" />
                Étape 4 : Analyse de fusion entre formations
              </h2>

              {/* Métriques principales */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    {comparisonData.fusedCount}
                  </div>
                  <div className="text-sm text-gray-700">
                    Compétences fusionnées en commun
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
                  <div className="text-3xl font-bold text-purple-600 mb-2">
                    {comparisonData.unificationRate}%
                  </div>
                  <div className="text-sm text-gray-700">
                    Taux d'unification
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-6 h-6 text-green-600" />
                    <div className="text-3xl font-bold text-green-600">
                      {comparisonData.timeSaved}h
                    </div>
                  </div>
                  <div className="text-sm text-gray-700">
                    Temps gagné (4h/compétence)
                  </div>
                </div>
              </div>

              {/* Message de recommandation */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg p-6">
                <h3 className="text-xl font-bold mb-3">
                  💡 Recommandation
                </h3>
                <p className="text-lg mb-2">
                  Fusionner ces 2 formations et unifier <strong>{comparisonData.unificationRate}%</strong> de vos formations
                </p>
                <p className="text-sm opacity-90">
                  Gain de temps estimé : <strong>{comparisonData.timeSaved} heures</strong> ({comparisonData.fusedCount} compétences × 4h)
                </p>
              </div>
            </div>

            {/* Comparaison côte-à-côte */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold">
                  📊 Comparaison détaillée
                </h3>

                {/* Toggle pour afficher uniquement les compétences fusionnées */}
                <button
                  onClick={() => setShowOnlyFused(!showOnlyFused)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    showOnlyFused
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {showOnlyFused ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  <span className="text-sm font-medium">
                    {showOnlyFused ? 'Compétences fusionnées uniquement' : 'Toutes les compétences'}
                  </span>
                </button>
              </div>

              {/* Grille côte-à-côte */}
              <div className="grid grid-cols-2 gap-6">
                {/* Formation 1 (gauche) */}
                <div className="border-2 border-blue-300 rounded-lg">
                  <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-lg">
                    <h4 className="font-bold text-lg">{comparisonData.formation1.title}</h4>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="px-3 py-1 bg-white bg-opacity-20 rounded text-sm">
                        {comparisonData.formation1.rncpCode}
                      </span>
                      <span className="text-sm opacity-90">
                        {comparisonData.formation1.competences.length} compétences
                      </span>
                    </div>
                  </div>

                  {/* Blocs de compétences */}
                  <div className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
                    {comparisonData.formation1.blocks && comparisonData.formation1.blocks.map((bloc, bIndex) => {
                      // Trouver les compétences fusionnées de ce bloc
                      const fusedInBloc = comparisonData.sharedFusions.filter(fusion => {
                        const source1 = fusion.sources.find(s => s.rncp === comparisonData.formation1.rncpCode);
                        return source1 && bloc.competences.includes(source1.text);
                      });

                      // Si showOnlyFused est activé, ne pas afficher le bloc s'il n'a pas de compétences fusionnées
                      if (showOnlyFused && fusedInBloc.length === 0) return null;

                      // Filtrer les compétences à afficher
                      const competencesToShow = showOnlyFused
                        ? bloc.competences.filter(comp =>
                            comparisonData.sharedFusions.some(fusion =>
                              fusion.sources.some(s => s.rncp === comparisonData.formation1.rncpCode && s.text === comp)
                            )
                          )
                        : bloc.competences;

                      if (competencesToShow.length === 0) return null;

                      return (
                        <div key={bIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gradient-to-r from-blue-100 to-blue-200 px-3 py-2">
                            <h5 className="text-sm font-semibold text-blue-900">
                              {bloc.code} - {bloc.libelle}
                            </h5>
                            <p className="text-xs text-blue-700 mt-1">
                              {showOnlyFused ? competencesToShow.length : bloc.competences.length} compétence{(showOnlyFused ? competencesToShow.length : bloc.competences.length) > 1 ? 's' : ''}
                              {fusedInBloc.length > 0 && (
                                <span className="ml-2 px-2 py-0.5 bg-green-600 text-white rounded-full">
                                  {fusedInBloc.length} fusionnée{fusedInBloc.length > 1 ? 's' : ''}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="p-3 bg-white">
                            <ul className="space-y-2">
                              {competencesToShow.map((comp, cIndex) => {
                                // Vérifier si cette compétence est fusionnée
                                const isFused = comparisonData.sharedFusions.some(fusion =>
                                  fusion.sources.some(s => s.rncp === comparisonData.formation1.rncpCode && s.text === comp)
                                );

                                return (
                                  <li key={cIndex} className={`flex items-start text-xs ${isFused ? 'bg-green-50 border-l-4 border-green-500 pl-2 py-1' : ''}`}>
                                    <span className={`inline-flex items-center justify-center w-5 h-5 mr-2 text-xs font-medium rounded-full flex-shrink-0 mt-0.5 ${isFused ? 'bg-green-600 text-white' : 'bg-blue-100 text-blue-600'}`}>
                                      {cIndex + 1}
                                    </span>
                                    <span className="text-gray-700 flex-1">
                                      {comp}
                                      {isFused && (
                                        <span className="ml-2 text-green-600 font-semibold">✓</span>
                                      )}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Formation 2 (droite) */}
                <div className="border-2 border-purple-300 rounded-lg">
                  <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 rounded-t-lg">
                    <h4 className="font-bold text-lg">{comparisonData.formation2.title}</h4>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="px-3 py-1 bg-white bg-opacity-20 rounded text-sm">
                        {comparisonData.formation2.rncpCode}
                      </span>
                      <span className="text-sm opacity-90">
                        {comparisonData.formation2.competences.length} compétences
                      </span>
                    </div>
                  </div>

                  {/* Blocs de compétences */}
                  <div className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
                    {comparisonData.formation2.blocks && comparisonData.formation2.blocks.map((bloc, bIndex) => {
                      // Trouver les compétences fusionnées de ce bloc
                      const fusedInBloc = comparisonData.sharedFusions.filter(fusion => {
                        const source2 = fusion.sources.find(s => s.rncp === comparisonData.formation2.rncpCode);
                        return source2 && bloc.competences.includes(source2.text);
                      });

                      // Si showOnlyFused est activé, ne pas afficher le bloc s'il n'a pas de compétences fusionnées
                      if (showOnlyFused && fusedInBloc.length === 0) return null;

                      // Filtrer les compétences à afficher
                      const competencesToShow = showOnlyFused
                        ? bloc.competences.filter(comp =>
                            comparisonData.sharedFusions.some(fusion =>
                              fusion.sources.some(s => s.rncp === comparisonData.formation2.rncpCode && s.text === comp)
                            )
                          )
                        : bloc.competences;

                      if (competencesToShow.length === 0) return null;

                      return (
                        <div key={bIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gradient-to-r from-purple-100 to-purple-200 px-3 py-2">
                            <h5 className="text-sm font-semibold text-purple-900">
                              {bloc.code} - {bloc.libelle}
                            </h5>
                            <p className="text-xs text-purple-700 mt-1">
                              {showOnlyFused ? competencesToShow.length : bloc.competences.length} compétence{(showOnlyFused ? competencesToShow.length : bloc.competences.length) > 1 ? 's' : ''}
                              {fusedInBloc.length > 0 && (
                                <span className="ml-2 px-2 py-0.5 bg-green-600 text-white rounded-full">
                                  {fusedInBloc.length} fusionnée{fusedInBloc.length > 1 ? 's' : ''}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="p-3 bg-white">
                            <ul className="space-y-2">
                              {competencesToShow.map((comp, cIndex) => {
                                // Vérifier si cette compétence est fusionnée
                                const isFused = comparisonData.sharedFusions.some(fusion =>
                                  fusion.sources.some(s => s.rncp === comparisonData.formation2.rncpCode && s.text === comp)
                                );

                                return (
                                  <li key={cIndex} className={`flex items-start text-xs ${isFused ? 'bg-green-50 border-l-4 border-green-500 pl-2 py-1' : ''}`}>
                                    <span className={`inline-flex items-center justify-center w-5 h-5 mr-2 text-xs font-medium rounded-full flex-shrink-0 mt-0.5 ${isFused ? 'bg-green-600 text-white' : 'bg-purple-100 text-purple-600'}`}>
                                      {cIndex + 1}
                                    </span>
                                    <span className="text-gray-700 flex-1">
                                      {comp}
                                      {isFused && (
                                        <span className="ml-2 text-green-600 font-semibold">✓</span>
                                      )}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Légende */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Légende :</h4>
                <div className="flex gap-4 text-sm text-gray-700">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-50 border-l-4 border-green-500"></div>
                    <span>Compétence fusionnée (commune aux 2 formations)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-white border border-gray-200"></div>
                    <span>Compétence unique à cette formation</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestFusionOnboarding;
